/**
 * Stabile Rueckgabewerte fuer Getter und Methoden, die im Template an ein @Input
 * gebunden sind.
 *
 * Warum es das braucht: Angular vergleicht Inputs ueber die Referenz. Ein Getter,
 * der bei jedem Aufruf ein neues Array oder Objekt liefert (`[...x]`, `.map()`,
 * `.filter()`, `{a, b}`), gilt damit in *jedem* Change-Detection-Durchlauf als
 * geaendert. Die Kind-Komponente rechnet jedes Mal neu, und im Development-Modus
 * findet `checkNoChanges` danach wieder ein anderes Objekt. Reagiert das Kind
 * teuer darauf, friert die Seite ein - so geschehen auf dem Dashboard (Task-
 * Kachel) und im Task-Board (cdkDropListData).
 *
 * `createMemo()` liefert pro Komponente einen Cache: dasselbe Ergebnis, solange
 * die angegebenen Abhaengigkeiten unveraendert sind.
 *
 * @example
 *   private readonly memo = createMemo();
 *
 *   get paletteModules(): ModuleManifest[] {
 *     return this.memo('paletteModules', [this.allModules],
 *       () => this.allModules.filter(m => !HIDDEN.has(m.moduleId)));
 *   }
 */
export type Memo = <T>(key: string, deps: ReadonlyArray<unknown>, compute: () => T) => T;

export function createMemo(): Memo {
  const cache = new Map<string, {deps: ReadonlyArray<unknown>; value: unknown}>();

  return <T>(key: string, deps: ReadonlyArray<unknown>, compute: () => T): T => {
    const hit = cache.get(key);
    if (hit && hit.deps.length === deps.length && hit.deps.every((dep, i) => dep === deps[i])) {
      return hit.value as T;
    }
    const value = compute();
    cache.set(key, {deps, value});
    return value;
  };
}

/**
 * Vergleichsschluessel fuer eine Abhaengigkeit, die in place veraendert werden kann.
 *
 * `createMemo` vergleicht Abhaengigkeiten mit `===`. Bei Arrays und Objekten heisst
 * das: Wird ein Array per `push` erweitert oder ein Feld eines Objekts ueberschrieben,
 * bleibt die Referenz gleich - der Cache merkt die Aenderung nicht und liefert den
 * alten Wert. Fuer solche Quellen deshalb den Inhalt als Schluessel verwenden.
 *
 * Bewusst nur fuer kleine Werte gedacht (Auswahllisten, einzelne Regelfelder): der
 * Schluessel wird in jedem Durchlauf neu gebildet.
 */
export function contentKey(value: unknown): unknown {
  return value !== null && typeof value === 'object' ? JSON.stringify(value) : value;
}
